import { useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { AddressableViewLayout } from 'framework/ViewTypes';
import { bindCollection } from 'framework/ViewTypes/contentLayout';
import { PositionProfileFormDrawer } from '../createupdate';
import {
  POSITION_PROFILE_CREATE,
  POSITION_PROFILE_DEPRECATE,
  POSITION_PROFILE_REACTIVATE,
  POSITION_PROFILE_RETIRE,
  POSITION_PROFILE_UPDATE,
} from '../constants';
import { usePositionProfilesUrl } from '../routing';
import type { PositionProfile, PositionProfileListRow } from '../types';
import usePositionProfilesService from '../usePositionProfilesService';
import { getPositionProfilesTableConfig } from './positionProfilesTableConfig';
import usePositionProfilesCollection from './usePositionProfilesCollection';
import usePositionProfilesFilterBar from './usePositionProfilesFilterBar';

const PositionProfilesListPage = () => {
  const { isGranted } = useAuthorization();
  const [isFormDrawerOpen, setFormDrawerOpen] = useState(false);
  const [profileToEdit, setProfileToEdit] = useState<PositionProfile>();

  const { getSingleEntityUrl } = usePositionProfilesUrl();
  const { getById, deprecate, reactivate, retire } = usePositionProfilesService();
  const collection = usePositionProfilesCollection();
  const { searchProps, filterProps } = usePositionProfilesFilterBar(collection);

  const openCreateDrawer = () => {
    setProfileToEdit(undefined);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = async (profile: PositionProfileListRow) => {
    const fullProfile = await getById(profile.id);
    setProfileToEdit(fullProfile);
    setFormDrawerOpen(true);
  };

  const changeStatus = async (profile: PositionProfileListRow, action: (id: string) => Promise<PositionProfile>) => {
    await action(profile.id);
    await collection.reload();
  };

  const binding = bindCollection(
    collection,
    getPositionProfilesTableConfig(collection.rows, getSingleEntityUrl, {
      canCreate: isGranted(POSITION_PROFILE_CREATE),
      canUpdate: isGranted(POSITION_PROFILE_UPDATE),
      canDeprecate: isGranted(POSITION_PROFILE_DEPRECATE),
      canReactivate: isGranted(POSITION_PROFILE_REACTIVATE),
      canRetire: isGranted(POSITION_PROFILE_RETIRE),
      onCreate: openCreateDrawer,
      onEdit: openEditDrawer,
      onDeprecate: profile => changeStatus(profile, deprecate),
      onReactivate: profile => changeStatus(profile, reactivate),
      onRetire: profile => changeStatus(profile, retire),
    }),
    { searchProps, filterProps },
  );

  return (
    <AddressableViewLayout
      {...binding.pageProps}
      drawer={
        <PositionProfileFormDrawer
          isOpen={isFormDrawerOpen}
          onClose={() => setFormDrawerOpen(false)}
          profile={profileToEdit}
          onSuccess={() => collection.reload()}
        />
      }
    />
  );
};

export default PositionProfilesListPage;
